// Tables and their QR codes (slice 64, ADR 0064): where an owner or manager makes
// the table QRs a guest scans to order, prints them, and replaces one that leaked.
//
// The server (slice 63) owns what a table is: a name the cashier recognises, and a
// random code that is part of the printed address. This page only turns those into
// something to put on a table. A code is a bearer value — a photograph of the QR
// works from anywhere — so replacing one ("Make a new QR") is a first-class action,
// and the page says plainly what that does and does not do.

import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client.js';
import type {
  BulkTablesResponse,
  MeResponse,
  OutletTable,
  OutletTableResponse,
  OutletTablesResponse,
} from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { hasPermission } from '../auth/permissions.js';
import { Chip, QueryState, actionErrorText } from './pos/common.js';
import { BULK_MAX, planBulkLabels } from './tables/bulk.js';
import {
  configuredMenuBase,
  download,
  fileSafe,
  normalizeMenuBase,
  qrPng,
  qrSvg,
  saveMenuBase,
  savedMenuBase,
  svgDataUri,
  tableUrl,
} from './tables/qr.js';

type Pending = { readonly kind: 'rotate' | 'retire'; readonly table: OutletTable };

const CONFIRM: Record<Pending['kind'], string> = {
  rotate:
    'This makes a new QR code for the table. The printed one stops working for new guests at once, so print the new one. Someone already seated can keep ordering until their sitting ends (up to three hours).',
  retire:
    'This takes the table out of use: its QR stops working for good and cannot be brought back. You can add a new table with the same name later.',
};

const TABLE_ERRORS: Readonly<Record<string, string>> = {
  table_label_taken: 'Another table here already has that name.',
  table_retired: 'That table has been taken out of use.',
  table_not_found: 'That table no longer exists. The list has been refreshed.',
};

function tableErrorText(err: unknown): string | null {
  const known = actionErrorText(err, 'Could not do that. Try again.');
  if (!known) return null;
  const code = (err as { body?: { code?: unknown } } | null)?.body?.code;
  if (typeof code === 'string' && TABLE_ERRORS[code]) return TABLE_ERRORS[code]!;
  if ((err as { status?: number } | null)?.status === 400) {
    return 'A table name is 1 to 20 characters, like T4 or Window 2.';
  }
  return known;
}

export function TablesPage(): JSX.Element {
  const { orgId, outletId } = useParams<{ orgId: string; outletId: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });
  const membership = me.data?.memberships.find((x) => x.outlet.id === outletId);
  const canEdit = membership ? hasPermission(membership.permissions, 'menu:edit') : false;
  const canSeeOrders = membership ? hasPermission(membership.permissions, 'pos:read') : false;

  const listKey = ['outlet-tables', orgId, outletId] as const;
  const tables = useQuery({
    queryKey: listKey,
    queryFn: () =>
      apiFetch<OutletTablesResponse>(`/organizations/${orgId}/outlets/${outletId}/tables`),
    enabled: !!orgId && !!outletId,
  });

  const envBase = configuredMenuBase();
  const [typedBase, setTypedBase] = useState(savedMenuBase() ?? '');
  const base = envBase ?? normalizeMenuBase(typedBase);

  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('T');
  const [from, setFrom] = useState('1');
  const [to, setTo] = useState('20');
  const [added, setAdded] = useState<BulkTablesResponse | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; label: string } | null>(null);

  const path = `/organizations/${orgId}/outlets/${outletId}/tables`;
  const done = (): void => {
    void qc.invalidateQueries({ queryKey: listKey });
  };
  const create = useMutation({
    mutationFn: (label: string) =>
      apiFetch<OutletTableResponse>(path, { method: 'POST', body: { label } }),
    onSuccess: () => {
      setName('');
      done();
    },
  });
  const createMany = useMutation({
    mutationFn: (labels: readonly string[]) =>
      apiFetch<BulkTablesResponse>(`${path}/bulk`, { method: 'POST', body: { labels } }),
    onSuccess: (res) => {
      setAdded(res);
      done();
    },
  });
  const rename = useMutation({
    mutationFn: (a: { id: string; label: string }) =>
      apiFetch<OutletTableResponse>(`${path}/${a.id}/rename`, {
        method: 'POST',
        body: { label: a.label },
      }),
    onSuccess: () => {
      setRenaming(null);
      done();
    },
  });
  const act = useMutation({
    mutationFn: (p: Pending) =>
      apiFetch<OutletTableResponse>(`${path}/${p.table.id}/${p.kind}`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      setPending(null);
      done();
    },
    onError: () => setPending(null),
  });
  const busy = create.isPending || createMany.isPending || rename.isPending || act.isPending;

  const all = tables.data?.tables ?? [];
  const live = all.filter((t) => t.state === 'active');
  const retired = all.filter((t) => t.state === 'retired');
  const printable = base ? live.filter((t) => t.qrPath) : [];
  const plan = planBulkLabels(prefix, from, to);
  const errorText = tableErrorText(create.error ?? createMany.error ?? rename.error ?? act.error);

  function onCreate(e: FormEvent): void {
    e.preventDefault();
    if (name.trim()) create.mutate(name.trim());
  }

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Tables &amp; QR codes</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> ·{' '}
            <Link to={`/orgs/${orgId}/outlets/${outletId}/menus`}>Back to menus</Link>
          </p>
        </div>
        <div className="row-actions no-print">
          {canSeeOrders ? (
            <Link
              className="button secondary"
              to={`/orgs/${orgId}/outlets/${outletId}/guest-orders`}
            >
              Guest orders
            </Link>
          ) : null}
          <button
            type="button"
            disabled={printable.length === 0}
            onClick={() => globalThis.print()}
            title={printable.length === 0 ? 'Add a table and the menu address first' : undefined}
          >
            Print all QR codes
          </button>
        </div>
      </header>

      <section className="card no-print" aria-labelledby="how-heading">
        <h2 id="how-heading">How it works</h2>
        <p>
          Guests scan the QR on their table, choose items on their phone and send them to the
          restaurant. <strong>Nothing reaches the kitchen until your cashier accepts it</strong> on
          the till, so a QR that someone photographs and sends elsewhere can only ever create a
          request that gets turned down. Name each table the way your cashier says it (T4) — that is
          the table the order lands on.
        </p>
      </section>

      <section className="card no-print" aria-labelledby="base-heading">
        <h2 id="base-heading">Public menu address</h2>
        {envBase ? (
          <p>
            QR codes open pages on <strong>{envBase}</strong>.
          </p>
        ) : (
          <>
            <p className="muted">
              The address your menu is served from — the one your menu QR already uses. It is
              different from this dashboard&apos;s address.
            </p>
            <div className="settings-form">
              <label htmlFor="menu-base">Menu address</label>
              <input
                id="menu-base"
                type="url"
                inputMode="url"
                placeholder="https://menu.your-restaurant.in"
                value={typedBase}
                onChange={(e) => {
                  setTypedBase(e.target.value);
                  const ok = normalizeMenuBase(e.target.value);
                  if (ok) saveMenuBase(ok);
                }}
              />
              {typedBase.trim() && !base ? (
                <p className="error" role="alert">
                  That is not a web address. Start it with https://
                </p>
              ) : null}
            </div>
          </>
        )}
      </section>

      <QueryState loading={tables.isLoading} error={tables.error} />

      {canEdit ? (
        <section className="card no-print" aria-labelledby="add-heading">
          <h2 id="add-heading">Add a table</h2>
          <form onSubmit={onCreate} className="row-actions">
            <label className="inline-field">
              Table name{' '}
              <input
                type="text"
                maxLength={20}
                placeholder="T4"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <button type="submit" disabled={busy || !name.trim()}>
              Add table
            </button>
          </form>
        </section>
      ) : null}

      {canEdit ? (
        <section className="card no-print" aria-labelledby="bulk-heading">
          <h2 id="bulk-heading">Add several tables</h2>
          <p className="muted">
            For a restaurant with a row of numbered tables. A name that is already in use is
            skipped, so you can run this again safely.
          </p>
          <form
            className="row-actions"
            onSubmit={(e) => {
              e.preventDefault();
              if (plan.ok) createMany.mutate(plan.labels);
            }}
          >
            <label className="inline-field">
              Name starts with{' '}
              <input
                type="text"
                maxLength={15}
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
              />
            </label>
            <label className="inline-field">
              From{' '}
              <input
                type="text"
                inputMode="numeric"
                size={4}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="inline-field">
              To{' '}
              <input
                type="text"
                inputMode="numeric"
                size={4}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <button type="submit" disabled={busy || !plan.ok}>
              {plan.ok
                ? `Add ${plan.labels.length} ${plan.labels.length === 1 ? 'table' : 'tables'}`
                : 'Add tables'}
            </button>
          </form>
          {plan.ok ? (
            <p className="muted">
              Will add {plan.labels[0]}
              {plan.labels.length > 1 ? ` to ${plan.labels[plan.labels.length - 1]}` : ''} (up to{' '}
              {BULK_MAX} at a time).
            </p>
          ) : (
            <p className="error" role="alert">
              {plan.reason}
            </p>
          )}
          {added ? (
            <p role="status">
              Added {added.created.length} {added.created.length === 1 ? 'table' : 'tables'}.
              {added.skipped.length > 0
                ? ` Skipped ${added.skipped.join(', ')} because ${
                    added.skipped.length === 1 ? 'that name is' : 'those names are'
                  } already in use.`
                : ''}
            </p>
          ) : null}
        </section>
      ) : null}

      {errorText ? (
        <p className="error no-print" role="alert">
          {errorText}
        </p>
      ) : null}

      {pending ? (
        <div
          className="card no-print"
          role="alertdialog"
          aria-label={`${pending.kind} ${pending.table.label}`}
        >
          <p>
            <strong>
              {pending.kind === 'rotate' ? 'Make a new QR for' : 'Take out of use'} “
              {pending.table.label}”?
            </strong>
          </p>
          <p>{CONFIRM[pending.kind]}</p>
          <div className="row-actions">
            <button type="button" disabled={busy} onClick={() => act.mutate(pending)}>
              {pending.kind === 'rotate' ? 'Yes, make a new QR' : 'Yes, take it out of use'}
            </button>
            <button type="button" className="secondary" onClick={() => setPending(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {tables.data && live.length === 0 ? (
        <p className="muted no-print">
          No tables yet.{' '}
          {canEdit ? 'Add one above.' : 'Ask an owner or manager to add the restaurant’s tables.'}
        </p>
      ) : null}

      <ul className="table-grid no-print">
        {live.map((t) => (
          <li key={t.id} className="card table-card">
            <div className="table-card-head">
              {renaming?.id === t.id ? (
                <form
                  className="row-actions"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (renaming.label.trim())
                      rename.mutate({ id: t.id, label: renaming.label.trim() });
                  }}
                >
                  <input
                    type="text"
                    aria-label={`New name for ${t.label}`}
                    maxLength={20}
                    value={renaming.label}
                    onChange={(e) => setRenaming({ id: t.id, label: e.target.value })}
                  />
                  <button type="submit" disabled={busy}>
                    Save
                  </button>
                  <button type="button" className="secondary" onClick={() => setRenaming(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                <h3>{t.label}</h3>
              )}
              <Chip tone="ok">In use</Chip>
            </div>
            {base && t.qrPath ? (
              <Qr label={t.label} url={tableUrl(base, t.qrPath)} />
            ) : (
              <p className="muted">Enter the menu address above to see this QR code.</p>
            )}
            {canEdit && renaming?.id !== t.id ? (
              <div className="row-actions">
                <button
                  type="button"
                  className="secondary"
                  disabled={busy}
                  onClick={() => setRenaming({ id: t.id, label: t.label })}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={busy}
                  onClick={() => setPending({ kind: 'rotate', table: t })}
                >
                  Make a new QR
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={busy}
                  onClick={() => setPending({ kind: 'retire', table: t })}
                >
                  Take out of use
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {retired.length > 0 ? (
        <section className="no-print" aria-labelledby="retired-heading">
          <h2 id="retired-heading">Taken out of use</h2>
          <p className="muted">{retired.map((t) => t.label).join(', ')}</p>
        </section>
      ) : null}

      <div className="print-sheet" aria-hidden="true">
        {printable.map((t) => (
          <div key={t.id} className="qr-card">
            <PrintQr url={tableUrl(base!, t.qrPath!)} />
            <div className="qr-card-label">{t.label}</div>
            <div className="qr-card-hint">Scan to order from your table</div>
          </div>
        ))}
      </div>
    </main>
  );
}

function useQrImage(url: string): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setSrc(null);
    qrSvg(url)
      .then((svg) => {
        if (live) setSrc(svgDataUri(svg));
      })
      .catch(() => {
        if (live) setSrc(null);
      });
    return () => {
      live = false;
    };
  }, [url]);
  return src;
}

function Qr({ label, url }: { readonly label: string; readonly url: string }): JSX.Element {
  const src = useQrImage(url);
  const [failed, setFailed] = useState(false);
  return (
    <div className="qr-block">
      {src ? (
        <img src={src} alt={`QR code for table ${label}`} width={160} height={160} />
      ) : (
        <div className="qr-placeholder" aria-hidden="true" />
      )}
      <div className="qr-meta">
        <code className="qr-url">{url}</code>
        <div className="row-actions">
          <a className="button secondary small" href={url} target="_blank" rel="noreferrer">
            Try it
          </a>
          <button
            type="button"
            className="secondary small"
            onClick={() => {
              void qrSvg(url).then((svg) => download(`qr-${fileSafe(label)}.svg`, svgDataUri(svg)));
            }}
          >
            Download SVG
          </button>
          <button
            type="button"
            className="secondary small"
            onClick={() => {
              qrPng(url)
                .then((png) => download(`qr-${fileSafe(label)}.png`, png))
                .catch(() => setFailed(true));
            }}
          >
            Download PNG
          </button>
        </div>
        {failed ? (
          <p className="error" role="alert">
            Could not make the PNG here. Use the SVG, or print from this page.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function PrintQr({ url }: { readonly url: string }): JSX.Element {
  const src = useQrImage(url);
  return src ? (
    <img src={src} alt="" className="qr-card-img" />
  ) : (
    <div className="qr-placeholder" />
  );
}
